import React from 'react'
import Chart from 'react-google-charts'
import { useSelector } from 'react-redux'
import { getYou, getShowWeather } from '../redux/options/selectors'
import { getFastestPerDay, getFastestPerGroup } from '../redux/data/selectors'
import { laptime2ms, time2seconds, getClockTicks } from '../tools/Time'
import { getLapColour } from '../tools/LapStatus'
import { getCarColours, isHighlighted } from '../tools/CarColours'
import ChartContainer from './ChartContainer'
import WeatherStrip, { WEATHER_HEIGHT, getTimeToAxis } from './WeatherStrip'
import WeatherForecast from './WeatherForecast'
import ChartLegend from './ChartLegend'
import { useChartTooltip } from './ChartTooltip'
import { LAPTIME_AXIS_WIDTH, getLaptimeAxis } from '../tools/ChartAxis'

/**
 * Horizontal axis by lap number of the car, for one car
 * @type {string}
 */
export const AXIS_LAP = 'lap'

/**
 * Horizontal axis by time of day, so laps of several cars each have their own place
 * @type {string}
 */
export const AXIS_TIME = 'time'

/**
 * Line and point size of the highlighted car, or of the only car
 * @type {Object<string, number>}
 */
const HIGHLIGHT_SERIES = { pointSize: 5, lineWidth: 2 }

/**
 * Line and point size of the other cars, thinner so the highlighted car stands out
 * @type {Object<string, number>}
 */
const OTHER_SERIES = { pointSize: 3, lineWidth: 1 }

/**
 * Narrowest zoom on each axis: two laps, or five minutes
 * @type {Object<string, number>}
 */
const MIN_RANGE = { [ AXIS_LAP ]: 2, [ AXIS_TIME ]: 5 * 60 }

/**
 * Most labels on the horizontal axis
 * @type {number}
 */
const MAX_TICKS = 8

/**
 * Space around the plot, as in chartArea; the left follows the laptime labels
 * @type {Object<string, number>}
 */
const AREA = { right: 10, bottom: 25 }

/**
 * Get the chart cells of one lap: the laptime and the point colour
 * @param {Object} row lap
 * @param {string} colour line colour of the car
 * @param {Object|null} fastestDay fastest lap of the day
 * @param {Array<Object>} fastestGroup fastest lap per group
 * @returns {Array} laptime and point style cells
 */
const getLapCells = ( row, colour, fastestDay, fastestGroup ) =>
{
  // dot colour as in the list
  const point = getLapColour( row, fastestDay, fastestGroup, colour )

  return [
    { v: laptime2ms( row.laptime ), f: row.laptime },
    `point { fill-color: ${ point }; stroke-color: ${ point }; }`
  ]
}

/**
 * Get the labels of the horizontal axis in the shown part: lap numbers, or times at a round step
 * @param {string} axis AXIS_LAP or AXIS_TIME
 * @param {Object} view shown part with min and max
 * @returns {Array<Object>} ticks with value and label
 */
const getTicks = ( axis, view ) =>
{
  if ( axis === AXIS_TIME ) return getClockTicks( view.min, view.max, MAX_TICKS )

  const step = Math.max( 1, Math.ceil( ( view.max - view.min ) / ( MAX_TICKS * 2 ) ) )
  const ticks = []

  for ( let v = Math.ceil( view.min / step ) * step; v <= view.max; v += step )
  {
    ticks.push( { v, f: String( v ) } )
  }

  return ticks
}

/**
 * Render the laptimes of one or more cars, one line per car
 * @param {Object} props component props
 * @param {Array<Object>} props.rows laps
 * @param {number} props.max_rows number of laps to show, 0 for all
 * @param {number|string} props.car chosen car, drawn in red like yours
 * @param {string} props.axis AXIS_LAP for lap numbers, AXIS_TIME for the time of day
 * @returns {JSX.Element|null} chart, nothing without enough laps
 */
const TimeChart = ( { rows = [], max_rows = 20, car = '', axis = AXIS_LAP } ) =>
{
  const fastestDay = useSelector( getFastestPerDay )
  const fastestGroup = useSelector( getFastestPerGroup )
  const you = useSelector( getYou )
  const showWeather = useSelector( getShowWeather )
  // the lap of a clicked point
  const { ref, onClick, onChartReady, close, tooltip } = useChartTooltip()

  // stop here if there's no data
  if ( !rows || rows.length <= 1 ) return null

  // each lap at its own place, oldest first
  const sorted = [ ...rows ].sort( ( a, b ) => a.time > b.time ? 1 : -1 )
  const laps = max_rows ? sorted.slice( -1 * Math.min( max_rows + 1, sorted.length ) ) : sorted

  if ( laps.length <= 1 ) return null

  // colour per car, the same as in the position chart; your car or the chosen car highlighted
  const colours = getCarColours( rows, [ car, you ] )
  const cars = Object.keys( colours ).filter( c => laps.some( row => String( row.car ) === c ) )

  // y-axis labels: fastest, average and slowest of all laps
  const laptimeAxis = getLaptimeAxis( rows.map( row => laptime2ms( row.laptime ) ) )

  // one row per lap: its place on the axis, then laptime and point style of its car, empty for the others
  const getX = row => axis === AXIS_TIME ? time2seconds( row.time ) : row.lap
  const data = [
    [ 'Lap', ...cars.flatMap( c => [ c, { role: 'style' } ] ) ],
    ...laps.map( row => [
      { v: getX( row ), f: axis === AXIS_TIME ? `${ row.time } · Lap ${ row.lap }` : 'Lap ' + row.lap },
      ...cars.flatMap( c => c === String( row.car ) ? getLapCells( row, colours[ c ], fastestDay, fastestGroup ) : [ null, null ] )
    ] )
  ]

  // range of the axis, to zoom in on
  const xs = laps.map( getX )
  const domain = { min: Math.min( ...xs ), max: Math.max( ...xs ) }

  // the only car, or the highlighted one, as on the car page; the others thinner
  const single = cars.length === 1
  const series = cars.map( c => ( {
    color: colours[ c ],
    ...( single || isHighlighted( colours, c ) ? HIGHLIGHT_SERIES : OTHER_SERIES )
  } ) )

  // room for the weather above the plot
  const top = 10 + ( showWeather ? WEATHER_HEIGHT : 0 )

  // time of each lap, for the weather
  const times = laps.map( row => time2seconds( row.time ) )

  // which line is which, below the chart
  const legend = cars.map( c => ( { label: c, colour: colours[ c ], highlighted: isHighlighted( colours, c ) } ) )

  return (
    <div className="chart-tooltip-area" ref={ ref } onClick={ onClick }>
      { tooltip }
      <ChartContainer
        onViewChange={ close }
        className="linechart chart time-chart"
        min={ domain.min }
        max={ domain.max }
        minRange={ MIN_RANGE[ axis ] }
        left={ LAPTIME_AXIS_WIDTH }
        right={ AREA.right }
        footer={ (
          <React.Fragment>
            <ChartLegend items={ legend }/>
            { showWeather && <WeatherForecast credit/> }
          </React.Fragment>
        ) }
      >
        { view => (
          <React.Fragment>
            { showWeather && (
              <WeatherStrip
                toX={ axis === AXIS_TIME ? undefined : getTimeToAxis( times, xs ) }
                from={ times[ 0 ] }
                to={ times[ times.length - 1 ] }
                view={ view || domain }
                left={ LAPTIME_AXIS_WIDTH }
                right={ AREA.right }
                top={ top }
                bottom={ AREA.bottom }
              />
            ) }
            <Chart
              width={ '100%' }
              height={ '30%' }
              chartType="LineChart"
              chartEvents={ [ { eventName: 'ready', callback: onChartReady( row => laps[ row ] ) } ] }
              options={ {
                series: series,
                tooltip: {
                  trigger: 'none'
                },
                interpolateNulls: true,
                legend: 'none',
                vAxis: {
                  ...laptimeAxis,
                  gridlines: {
                    color: '#999'
                  }
                },
                hAxis: {
                  ticks: getTicks( axis, view || domain ),
                  viewWindow: view || undefined,
                  gridlines: {
                    color: '#eee'
                  }
                },
                chartArea: {
                  left: LAPTIME_AXIS_WIDTH,
                  top: top,
                  right: AREA.right,
                  bottom: AREA.bottom
                }
              } }
              data={ data }
            />
          </React.Fragment>
        ) }
      </ChartContainer>
    </div>
  )
}

export default TimeChart
